import { Colors } from '@vetahealth/fishing-gear/colors'
import {
  NotificationChannelEnum,
  NotificationEventTypeEnum,
  PatientActivityNotification,
  PatientActivityTask,
  PatientActivityTaskResponseStatusEnum,
  PatientActivityTracking,
  PatientActivityTypeEnum,
  TaskTypeEnum,
} from '@vetahealth/tuna-can-api'
import dayjs from 'dayjs'
import i18next from 'i18next'
import { DefaultTheme } from 'styled-components'

import { getTrackingTitle } from '../../helpers'
import { PatientActivity } from './types'

export function isTaskItem(item: PatientActivity): item is PatientActivityTask {
  return item.type === PatientActivityTypeEnum.Task
}

export function isTrackingItem(item: PatientActivity): item is PatientActivityTracking {
  return item.type === PatientActivityTypeEnum.Tracking
}

export function isNotificationItem(item: PatientActivity): item is PatientActivityNotification {
  return item.type === PatientActivityTypeEnum.Notification
}

function getTaskTitle(type: TaskTypeEnum): string {
  const translations: Record<TaskTypeEnum, string> = {
    information: i18next.t('activity.information'),
    survey: i18next.t('activity.survey'),
  }
  return translations[type]
}

function getNotificationType(type: NotificationEventTypeEnum): string {
  const translations: Record<NotificationEventTypeEnum, string> = {
    measurementReminder: i18next.t('activity.measurementReminder'),
    measurementResponse: i18next.t('activity.measurementResponse'),
    taskReminder: i18next.t('activity.taskReminder'),
    general: i18next.t('activity.generalReminder'),
  }
  return translations[type]
}

function getNotificationTitle(type: NotificationChannelEnum): string {
  const translations: Record<NotificationChannelEnum, string> = {
    email: i18next.t('activity.email', { activity: undefined }),
    push: i18next.t('activity.push', { activity: undefined }),
    sms: i18next.t('activity.sms', { activity: undefined }),
    voice: i18next.t('activity.call', { activity: undefined }),
  }
  return translations[type]
}

export function getCardColor(item: PatientActivity, theme: DefaultTheme): { background: Colors; active: Colors } {
  const mapPatientActivityTypeToCardColor = {
    [PatientActivityTypeEnum.Task]: {
      background: theme.taskBackground,
      active: theme.taskActive,
    },
    [PatientActivityTypeEnum.Tracking]: {
      background: theme.trackingBackground,
      active: theme.trackingActive,
    },
    [PatientActivityTypeEnum.Notification]: {
      background: theme.reminderBackground,
      active: theme.reminderActive,
    },
  }
  return mapPatientActivityTypeToCardColor[item.type]
}

export function getActivityType(item: PatientActivity): string | undefined {
  if (isTaskItem(item)) return i18next.t('activity.content')
  if (isTrackingItem(item)) return i18next.t('activity.measurement')
  if (isNotificationItem(item)) return getNotificationType(item.notificationType)
}

export function getTitle(item: PatientActivity): string | undefined {
  if (isTaskItem(item)) return getTaskTitle(item.taskType)
  if (isTrackingItem(item)) return getTrackingTitle(item.trackingType)
  if (isNotificationItem(item)) return getNotificationTitle(item.channel)
}

export function getTaskStatus(item: PatientActivityTask): string {
  return {
    [PatientActivityTaskResponseStatusEnum.Answered]: i18next.t('tracking.task.completed', {
      time: dayjs(item.timestamp).format('ll'),
    }),
    [PatientActivityTaskResponseStatusEnum.Skipped]: i18next.t('tracking.task.skipped'),
  }[item.responseStatus]
}
